import { useResidentContext } from "app/features/residents/provider/ResidentProvider";
import { useGetDiscontinuedMedicationsQuery } from "app/services/api/medicationsApi";
import { RootState } from "app/services/store";
import { selectResidentDiscontinuedMedications } from "app/store/slices/medications";
import { selectIsOnline } from "app/store/slices/network";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import usePagination from "ui/molecules/pagination/usePagination";
import { useMedicationFrequencyFilter } from "app/features/medications/hooks/useMedicationFrequencyFilter";
import { MedicationIndexPaginatedResponse } from "app/services/api/types/medications/responses/MedicationIndexResponse";

export function useMedicationHistoryWithOffline() {
  const { residentId } = useResidentContext();
  const { handlePageChange, currentPage } = usePagination();
  const { frequencies, handleFrequencyChange } = useMedicationFrequencyFilter();
  const isOnline = useSelector(selectIsOnline);

  const offlineData = useSelector((state: RootState) =>
    selectResidentDiscontinuedMedications(state, residentId),
  );

  const {
    data: onlineData,
    isFetching,
    isError,
  } = useGetDiscontinuedMedicationsQuery(
    {
      filter: {
        resident_id: residentId,
        shift: frequencies,
      },
      page: currentPage,
    },
    { skip: !residentId },
  );

  const data = useMemo(() => {
    if (isFetching || isOnline)
      return (onlineData as MedicationIndexPaginatedResponse)?.data;
    if (isError && offlineData) return offlineData;
    return (onlineData as MedicationIndexPaginatedResponse)?.data;
  }, [onlineData, offlineData, isOnline, isFetching]);

  return {
    data,
    meta: (onlineData as MedicationIndexPaginatedResponse)?.meta,
    isFetching,
    handlePageChange,
    frequencies,
    handleFrequencyChange,
  };
}
